/**
 * AIAssistantOverlay — DRR-309 Phase 9
 *
 * AI-powered suggestions panel for workflows.
 * Shows next steps, field value suggestions, and routing recommendations.
 *
 * Integrates with existing LLM infrastructure:
 * - useLlmTaskPrefill for form auto-population
 * - OptimizationRecommendationViewer patterns
 */

import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Progress,
  Tooltip,
  ActionIcon,
  Collapse,
  Alert,
  Divider,
  Loader,
} from '@mantine/core';
import {
  IconBrain,
  IconBulb,
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconWand,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useJourneyOverlay } from './JourneyOverlayProvider';
import type { AISuggestion } from './types';

// ============================================================================
// GraphQL Query
// ============================================================================

const JOURNEY_AI_SUGGESTIONS = gql`
  query JourneyAiSuggestions($tenantId: String!, $instanceId: String!, $limit: Int) {
    journeyAiSuggestions(tenantId: $tenantId, instanceId: $instanceId, limit: $limit) {
      id
      suggestionType
      title
      description
      priority
      confidence
      actionType
      actionData
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface AIAssistantOverlayProps {
  /** Tenant ID for queries */
  tenantId?: string;
  /** Instance ID for queries */
  instanceId?: string;
  /** Compact mode for embedding in headers */
  compact?: boolean;
  /** Callback when suggestion is accepted */
  onAccept?: (suggestion: AISuggestion) => void;
  /** Callback when suggestion is dismissed */
  onDismiss?: (suggestionId: string) => void;
}

// ============================================================================
// Confidence Color Helper
// ============================================================================

function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return 'green';
  if (confidence >= 70) return 'blue';
  if (confidence >= 50) return 'yellow';
  return 'gray';
}

function getTypeIcon(type: AISuggestion['type']): React.ReactNode {
  switch (type) {
    case 'next_action':
      return <IconBulb size={16} />;
    case 'field_value':
      return <IconWand size={16} />;
    case 'routing':
      return <IconBrain size={16} />;
    case 'warning':
      return <IconAlertTriangle size={16} />;
    default:
      return <IconBrain size={16} />;
  }
}

function getTypeColor(type: AISuggestion['type']): string {
  switch (type) {
    case 'next_action':
      return 'blue';
    case 'field_value':
      return 'violet';
    case 'routing':
      return 'cyan';
    case 'warning':
      return 'orange';
    default:
      return 'gray';
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function AIAssistantOverlay({
  tenantId,
  instanceId,
  compact = false,
  onAccept,
  onDismiss,
}: AIAssistantOverlayProps) {
  const { isOverlayActive } = useJourneyOverlay();

  const [expanded, setExpanded] = useState(true);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Fetch AI suggestions from backend
  const { data, loading } = useQuery(JOURNEY_AI_SUGGESTIONS, {
    variables: { tenantId: tenantId ?? '', instanceId: instanceId ?? '', limit: 10 },
    skip: !tenantId || !instanceId || !isOverlayActive('AIAssistant'),
    pollInterval: 60000, // Refresh suggestions every minute
  });

  if (!isOverlayActive('AIAssistant')) {
    return null;
  }

  // Map backend response to AISuggestion type
  const suggestions: AISuggestion[] = (data?.journeyAiSuggestions ?? []).map(
    (s: { id: string; suggestionType: string; title: string; description: string; priority: string; confidence: number; actionType?: string; actionData?: unknown }) => ({
      id: s.id,
      type: s.suggestionType as AISuggestion['type'],
      title: s.title,
      description: s.description,
      confidence: Math.round(s.confidence * 100),
      action: s.actionType ? { type: s.actionType, params: s.actionData } : undefined,
    })
  );

  const activeSuggestions = suggestions.filter(
    (s) => !dismissedIds.has(s.id)
  );

  const handleAccept = (suggestion: AISuggestion) => {
    onAccept?.(suggestion);
    setDismissedIds((prev) => new Set([...prev, suggestion.id]));
  };

  const handleDismiss = (suggestionId: string) => {
    onDismiss?.(suggestionId);
    setDismissedIds((prev) => new Set([...prev, suggestionId]));
  };

  if (compact) {
    return (
      <CompactAIBadge
        suggestionCount={activeSuggestions.length}
        topConfidence={activeSuggestions[0]?.confidence}
      />
    );
  }

  if (loading) {
    return (
      <Paper p="md" withBorder>
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading AI suggestions...</Text>
        </Group>
      </Paper>
    );
  }

  if (activeSuggestions.length === 0) {
    return null;
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon size="md" variant="light" color="violet" radius="xl">
              <IconBrain size={18} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                AI Assistant
              </Text>
              <Text size="xs" c="dimmed">
                {activeSuggestions.length} suggestion
                {activeSuggestions.length !== 1 ? 's' : ''}
              </Text>
            </div>
          </Group>
          <ActionIcon variant="subtle" onClick={() => setExpanded(!expanded)}>
            {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
        </Group>

        {/* Suggestions */}
        <Collapse in={expanded}>
          <Stack gap="xs">
            {activeSuggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onAccept={() => handleAccept(suggestion)}
                onDismiss={() => handleDismiss(suggestion.id)}
              />
            ))}
          </Stack>
        </Collapse>

        {/* Disclaimer */}
        <Alert
          icon={<IconInfoCircle size={14} />}
          color="gray"
          variant="light"
          p="xs"
        >
          <Text size="xs" c="dimmed">
            AI suggestions are based on pattern analysis and may require human
            review before action.
          </Text>
        </Alert>
      </Stack>
    </Paper>
  );
}

// ============================================================================
// Suggestion Card
// ============================================================================

interface SuggestionCardProps {
  suggestion: AISuggestion;
  onAccept: () => void;
  onDismiss: () => void;
}

function SuggestionCard({ suggestion, onAccept, onDismiss }: SuggestionCardProps) {
  const [showExplanation, setShowExplanation] = useState(false);
  const typeColor = getTypeColor(suggestion.type);
  const confidenceColor = getConfidenceColor(suggestion.confidence);

  return (
    <Card padding="sm" withBorder>
      <Stack gap="xs">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size="sm" variant="light" color={typeColor}>
              {getTypeIcon(suggestion.type)}
            </ThemeIcon>
            <Text size="sm" fw={500} truncate>
              {suggestion.title}
            </Text>
          </Group>

          <Tooltip label={`${suggestion.confidence}% confidence`}>
            <Badge size="xs" variant="light" color={confidenceColor}>
              {suggestion.confidence}%
            </Badge>
          </Tooltip>
        </Group>

        {/* Confidence bar */}
        <Progress
          value={suggestion.confidence}
          color={confidenceColor}
          size="xs"
          radius="xl"
        />

        {/* Description */}
        <Text size="xs" c="dimmed" lineClamp={showExplanation ? undefined : 2}>
          {suggestion.description}
        </Text>

        {suggestion.description.length > 100 && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={() => setShowExplanation(!showExplanation)}
          >
            {showExplanation ? 'Show less' : 'Why this suggestion?'}
          </Button>
        )}

        {/* Actions */}
        <Group gap="xs">
          {suggestion.action && (
            <Button
              size="xs"
              variant="light"
              color={typeColor}
              leftSection={<IconCheck size={12} />}
              onClick={onAccept}
            >
              Apply
            </Button>
          )}
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={12} />}
            onClick={onDismiss}
          >
            Dismiss
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ============================================================================
// Compact Badge
// ============================================================================

interface CompactAIBadgeProps {
  suggestionCount: number;
  topConfidence?: number;
}

function CompactAIBadge({ suggestionCount, topConfidence }: CompactAIBadgeProps) {
  if (suggestionCount === 0) return null;

  const confidenceColor = topConfidence
    ? getConfidenceColor(topConfidence)
    : 'violet';

  return (
    <Tooltip label={`${suggestionCount} AI suggestion${suggestionCount !== 1 ? 's' : ''}`}>
      <Badge
        size="sm"
        variant="light"
        color={confidenceColor}
        leftSection={<IconBrain size={12} />}
      >
        {suggestionCount}
      </Badge>
    </Tooltip>
  );
}

// ============================================================================
// Header Badge
// ============================================================================

interface AIAssistantBadgeProps {
  readonly tenantId?: string;
  readonly instanceId?: string;
}

export function AIAssistantBadge({ tenantId, instanceId }: AIAssistantBadgeProps) {
  const { activateOverlay, isOverlayActive, aiSuggestions } = useJourneyOverlay();

  // Use context suggestions count (populated by parent query)
  const suggestionCount = aiSuggestions.length;

  if (suggestionCount === 0) return null;

  return (
    <Badge
      size="sm"
      variant={isOverlayActive('AIAssistant') ? 'filled' : 'light'}
      color="violet"
      leftSection={<IconBrain size={12} />}
      style={{ cursor: 'pointer' }}
      onClick={() => activateOverlay('AIAssistant')}
    >
      {suggestionCount} AI
    </Badge>
  );
}

export default AIAssistantOverlay;
